import { importRowSchema, type Animal, type ImportResult, type ImportRow, type ProductKind } from '@bifeai/shared';
import { Badge, Button, Callout, Dialog } from '@bifeai/ui';
import { Download, FileSpreadsheet } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { formatQty, parseDecimal } from '../../lib/units';

const MAX_ROWS = 2000;

/** Encabezados de la planilla modelo. Se aceptan variantes (sin tildes, mayúsculas, etc.). */
const COLUMNS = [
  {
    key: 'name',
    title: 'Nombre',
    aliases: ['nombre', 'producto', 'descripcion'],
  },
  { key: 'category', title: 'Categoría', aliases: ['categoria', 'rubro'] },
  {
    key: 'unitType',
    title: 'Se vende por',
    aliases: ['se vende por', 'venta', 'unidad de venta', 'unidad'],
  },
  { key: 'kind', title: 'Tipo', aliases: ['tipo'] },
  { key: 'animal', title: 'Animal', aliases: ['animal'] },
  {
    key: 'plu',
    title: 'Código de balanza',
    aliases: ['codigo de balanza', 'plu', 'codigo balanza'],
  },
  {
    key: 'sku',
    title: 'Código interno',
    aliases: ['codigo interno', 'sku', 'codigo de barras', 'codigo'],
  },
  {
    key: 'price',
    title: 'Precio',
    aliases: ['precio', 'precio de venta', 'precio venta'],
  },
  {
    key: 'minStock',
    title: 'Stock mínimo',
    aliases: ['stock minimo', 'minimo'],
  },
  {
    key: 'tracksExpiry',
    title: 'Controla vencimiento',
    aliases: ['controla vencimiento', 'vencimiento', 'vence'],
  },
] as const;
type ColumnKey = (typeof COLUMNS)[number]['key'];

const norm = (v: unknown) =>
  String(v ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

function parseUnit(v: unknown): 'WEIGHT' | 'UNIT' | null {
  const s = norm(v);
  if (!s) return 'WEIGHT';
  if (/^(kg|kilo|kilos|peso|por kilo|x kg|kilogramo)/.test(s)) return 'WEIGHT';
  if (/^(u|un|unidad|unidades|por unidad|x u|pieza)/.test(s)) return 'UNIT';
  return null;
}
function parseKind(v: unknown): ProductKind | null {
  const s = norm(v);
  if (!s || s.startsWith('carne')) return 'MEAT';
  if (s.startsWith('sub') || s.includes('grasa') || s.includes('hueso')) return 'BYPRODUCT';
  if (s.startsWith('elab')) return 'ELABORATED';
  if (s.startsWith('alma') || s.startsWith('otro') || s.startsWith('gral') || s.startsWith('general')) return 'GENERAL';
  return null;
}
function parseAnimal(v: unknown): Animal | null {
  const s = norm(v);
  if (!s || s === '-') return 'NONE';
  if (/^(vac|novill|ternera|res|bovin)/.test(s)) return 'BEEF';
  if (/^(cerd|porc|lech)/.test(s)) return 'PORK';
  if (/^(poll|ave|gallina)/.test(s)) return 'CHICKEN';
  if (s.startsWith('otro') || s.startsWith('cord') || s.startsWith('chiv')) return 'OTHER';
  if (s.startsWith('no')) return 'NONE';
  return null;
}
function parseBool(v: unknown): boolean | null {
  const s = norm(v);
  if (!s) return null;
  if (['si', 's', 'yes', 'true', '1', 'x'].includes(s)) return true;
  if (['no', 'n', 'false', '0'].includes(s)) return false;
  return null;
}
function cellText(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = (typeof v === 'number' ? String(Math.trunc(v)) : String(v)).trim();
  return s || null;
}

interface ParsedRow {
  line: number;
  row?: ImportRow;
  error?: string;
}

function parseSheet(data: unknown[][]): {
  rows: ParsedRow[];
  missing: string[];
} {
  const header = (data[0] ?? []).map(norm);
  const index = {} as Record<ColumnKey, number>;
  for (const col of COLUMNS) {
    index[col.key] = header.findIndex((h) => (col.aliases as readonly string[]).includes(h) || h === norm(col.title));
  }
  const missing = index.name < 0 ? ['Nombre'] : [];
  const rows: ParsedRow[] = [];
  data.slice(1).forEach((cells, i) => {
    const get = (k: ColumnKey) => (index[k] >= 0 ? cells[index[k]] : null);
    if (cells.every((c) => c === null || c === undefined || String(c).trim() === '')) return;
    const line = i + 2;
    const unitType = parseUnit(get('unitType'));
    const kind = parseKind(get('kind'));
    const animal = parseAnimal(get('animal'));
    if (!unitType)
      return rows.push({
        line,
        error: 'En “Se vende por” poné kilo o unidad.',
      });
    if (!kind)
      return rows.push({
        line,
        error: 'Tipo inválido: carne, subproducto, elaborado o almacén.',
      });
    if (!animal)
      return rows.push({
        line,
        error: 'Animal inválido: vaca, cerdo, pollo, otro o no aplica.',
      });
    const priceRaw = get('price');
    const price = priceRaw === null || priceRaw === '' ? null : parseDecimal(priceRaw as string | number);
    if (priceRaw !== null && priceRaw !== '' && (price === null || price < 0)) return rows.push({ line, error: 'Precio inválido.' });
    const minRaw = get('minStock');
    const min = minRaw === null || minRaw === '' ? null : parseDecimal(minRaw as string | number);
    if (minRaw !== null && minRaw !== '' && (min === null || min < 0)) return rows.push({ line, error: 'Stock mínimo inválido.' });

    const parsed = importRowSchema.safeParse({
      name: cellText(get('name')) ?? '',
      category: cellText(get('category')),
      unitType,
      kind,
      animal,
      plu: cellText(get('plu')),
      sku: cellText(get('sku')),
      priceCents: price === null ? null : Math.round(price * 100),
      minStock: min === null ? null : unitType === 'WEIGHT' ? Math.round(min * 1000) : Math.round(min),
      tracksExpiry: parseBool(get('tracksExpiry')),
    });
    if (parsed.success) rows.push({ line, row: parsed.data });
    else
      rows.push({
        line,
        error: parsed.error.issues[0]?.message ?? 'Fila inválida.',
      });
  });
  return { rows, missing };
}

export async function downloadTemplate(): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const head = COLUMNS.map((c) => ({
    value: c.title,
    fontWeight: 'bold' as const,
    backgroundColor: '#E6F4FC',
  }));
  const sample: (string | number)[][] = [
    ['Asado de tira', 'Vacuna', 'Kilo', 'Carne', 'Vaca', '101', '', 14500, 10, 'Sí'],
    ['Bondiola', 'Cerdo', 'Kilo', 'Carne', 'Cerdo', '205', '', 11800, 5, 'Sí'],
    ['Chorizo puro cerdo', 'Embutidos', 'Kilo', 'Elaborado', 'Cerdo', '310', '', 9800, 3, 'Sí'],
    ['Carbón 4 kg', 'Almacén', 'Unidad', 'Almacén', 'No aplica', '', '7790001234567', 6500, 10, 'No'],
  ];
  await writeXlsxFile([head, ...sample], {
    sheet: 'Productos',
    columns: [
      { width: 26 },
      { width: 16 },
      { width: 14 },
      { width: 14 },
      { width: 12 },
      { width: 18 },
      { width: 18 },
      { width: 12 },
      { width: 14 },
      { width: 22 },
    ],
  }).toFile('bifeai-productos-modelo.xlsx');
}

export function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: (r: ImportResult) => Promise<void> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  async function load(file: File | undefined) {
    if (!file) return;
    setError(null);
    setParsed(null);
    if (!/\.xlsx$/i.test(file.name)) return setError('Subí un archivo de Excel (.xlsx). Si tenés .xls, guardalo como .xlsx.');
    if (file.size > 5 * 1024 * 1024) return setError('El archivo pesa más de 5 MB.');
    setBusy(true);
    try {
      const { readSheet } = await import('read-excel-file/browser');
      const data = (await readSheet(file)) as unknown[][];
      const { rows, missing } = parseSheet(data);
      if (missing.length) return setError(`Falta la columna ${missing.join(', ')}. Usá la planilla modelo.`);
      if (rows.length === 0) return setError('La planilla no tiene productos.');
      if (rows.length > MAX_ROWS) return setError(`Como máximo ${MAX_ROWS} productos por vez.`);
      setFileName(file.name);
      setParsed(rows);
    } catch {
      setError('No pudimos leer el archivo. Revisá que sea un Excel válido.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const valid = parsed?.filter((r) => r.row).map((r) => r.row!) ?? [];
  const invalid = parsed?.filter((r) => r.error) ?? [];

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ImportResult>('/products/import', {
        rows: valid,
      });
      await onDone(res);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setOver(false);
    void load(e.dataTransfer.files[0]);
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Importar productos desde Excel"
      description="Ideal para la carga inicial. Si un producto ya existe (mismo código de balanza, código interno o nombre) se actualiza."
      wide
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}

        {!parsed && (
          <>
            <div
              className="bf-dropzone"
              data-over={over}
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={onDrop}
            >
              <FileSpreadsheet />
              <strong>{busy ? 'Leyendo…' : 'Elegí o arrastrá el archivo .xlsx'}</strong>
              <span>Una fila por producto. La primera fila son los títulos.</span>
            </div>
            <Button variant="ghost" icon={<Download />} onClick={() => void downloadTemplate()} style={{ justifySelf: 'start' }}>
              Descargar planilla modelo
            </Button>
          </>
        )}

        {parsed && (
          <>
            <p>
              <strong>{fileName}</strong>: {valid.length} {valid.length === 1 ? 'producto listo' : 'productos listos'}
              {invalid.length > 0 && <>, {invalid.length} con errores</>}.
            </p>
            {invalid.length > 0 && (
              <Callout tone="warning">Las filas con errores no se importan. Podés corregirlas en el Excel y volver a subirlo.</Callout>
            )}
            <div className="bf-scroll-box">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Precio</th>
                    <th>Mínimo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((r) => (
                    <tr key={r.line}>
                      <td className="bf-cell-sub">{r.line}</td>
                      {r.row ? (
                        <>
                          <td className="bf-cell-main">{r.row.name}</td>
                          <td>{r.row.category ?? '—'}</td>
                          <td className="bf-num">{r.row.priceCents != null ? formatMoney(r.row.priceCents) : '—'}</td>
                          <td className="bf-num">{r.row.minStock ? formatQty(r.row.unitType, r.row.minStock) : '—'}</td>
                          <td>
                            <Badge tone="success">OK</Badge>
                          </td>
                        </>
                      ) : (
                        <td colSpan={5} className="bf-text-danger">
                          {r.error}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <input
          ref={inputRef}
          className="bf-file-input"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => void load(e.target.files?.[0])}
        />

        <div className="bf-form-actions">
          {parsed && (
            <Button variant="ghost" onClick={() => setParsed(null)} disabled={busy} style={{ marginRight: 'auto' }}>
              Elegir otro archivo
            </Button>
          )}
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          {parsed && (
            <Button variant="primary" loading={busy} disabled={valid.length === 0} onClick={() => void submit()}>
              Importar {valid.length}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
